import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, 
  Award, ArrowLeft, Calendar, FileText, CreditCard, 
  Shield, Eye, Download, Clock, CheckCircle, AlertTriangle,
  RefreshCw, History
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const MemberProfilePage = () => {
  const { userId } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberProfile();
  }, [userId]);

  const fetchMemberProfile = async () => {
    try {
      const response = await axios.get(`${API}/directory/${userId}`, { withCredentials: true });
      setMember(response.data);
    } catch (error) {
      console.error("Error fetching member profile:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <User className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="font-heading font-semibold text-xl text-slate-900 mb-2">
          Member Not Found
        </h2>
        <p className="text-slate-600 mb-4">
          This profile may no longer be available
        </p>
        <Link to="/directory">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Directory
          </Button>
        </Link>
      </div>
    );
  }

  const personal = member.personal_details || {};
  const work = member.work_details || {};
  const academic = member.academic_details || {};
  const professional = member.professional_details || {};
  const isAdminView = member.is_admin_view === true;
  const membershipStatus = member.membership_status || {};
  const directoryConsent = member.directory_consent || {};
  const applicationInfo = member.application_info || {};

  const handleViewDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/secure/file/${doc.file_id}/view`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Access denied or file not found");
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/secure/file/${doc.file_id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Access denied or file not found");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="member-profile-page">
      {/* Back Navigation */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link 
            to="/directory" 
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
            data-testid="back-to-directory"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Directory
          </Link>
          {isAdminView && (
            <Badge className="bg-purple-100 text-purple-800">
              <Shield className="w-3 h-3 mr-1" /> Admin View
            </Badge>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Card */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <User className="w-12 h-12 text-teal-700" />
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 mb-2">
                  {member.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full capitalize">
                    {member.membership_type?.replace('_', ' ')} Member
                  </span>
                  {member.speciality && (
                    <span className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                      {member.speciality}
                    </span>
                  )}
                  {isAdminView && membershipStatus.status && (
                    <Badge className={membershipStatus.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                      {membershipStatus.status?.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
                
                {/* Contact Info */}
                <div className="space-y-2">
                  {member.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${member.email}`} className="hover:text-teal-700">
                        {member.email}
                      </a>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${member.phone}`} className="hover:text-teal-700">
                        {member.phone}
                      </a>
                    </div>
                  )}
                  {(personal.city || personal.state || personal.country) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>
                        {[personal.city, personal.state, personal.country].filter(Boolean).join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ADMIN ONLY: Membership & Renewal Status */}
        {isAdminView && membershipStatus.membership_number && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Award className="w-5 h-5 text-teal-700" />
                Membership & Renewal Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Membership Number</p>
                  <p className="font-mono font-medium">{membershipStatus.membership_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Approval Date</p>
                  <p>{membershipStatus.approval_date ? new Date(membershipStatus.approval_date).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Valid Until</p>
                  <p className={membershipStatus.is_lifetime ? 'text-teal-700 font-medium' : ''}>
                    {membershipStatus.is_lifetime ? 'Life-Time' : membershipStatus.valid_until ? new Date(membershipStatus.valid_until).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Renewal Term</p>
                  <p>{membershipStatus.renewal_term || 1} year(s)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Directory Visibility/Consent */}
        {isAdminView && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Eye className="w-5 h-5 text-blue-600" />
                Directory Visibility & Consent
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {directoryConsent.is_directory_visible ? 
                    <CheckCircle className="w-4 h-4 text-emerald-600" /> : 
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  }
                  <span>Directory Visible: {directoryConsent.is_directory_visible ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {directoryConsent.email_display_permitted ? 
                    <CheckCircle className="w-4 h-4 text-emerald-600" /> : 
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  }
                  <span>Email Display: {directoryConsent.email_display_permitted ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {directoryConsent.phone_display_permitted ? 
                    <CheckCircle className="w-4 h-4 text-emerald-600" /> : 
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  }
                  <span>Phone Display: {directoryConsent.phone_display_permitted ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Full Personal Details */}
        {isAdminView && (personal.date_of_birth || personal.gender || personal.house_number) && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-slate-700" />
                Personal Details (Admin View)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {personal.date_of_birth && (
                  <div>
                    <p className="text-slate-500">Date of Birth</p>
                    <p>{personal.date_of_birth}</p>
                  </div>
                )}
                {personal.gender && (
                  <div>
                    <p className="text-slate-500">Gender</p>
                    <p className="capitalize">{personal.gender}</p>
                  </div>
                )}
                {(personal.house_number || personal.street_address) && (
                  <div className="col-span-2">
                    <p className="text-slate-500">Full Address</p>
                    <p>{[personal.house_number, personal.street_address, personal.city, personal.state, personal.pin_code, personal.country].filter(Boolean).join(', ')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Work Details */}
        {(work.organization_name || work.designation) && (
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-teal-700" />
                <h2 className="font-heading font-semibold text-lg text-slate-900">
                  Current Work
                </h2>
              </div>
              <div className="space-y-3">
                {work.designation && (
                  <div>
                    <p className="text-sm text-slate-500">Designation</p>
                    <p className="text-slate-900">{work.designation}</p>
                  </div>
                )}
                {work.organization_name && (
                  <div>
                    <p className="text-sm text-slate-500">Organization</p>
                    <p className="text-slate-900">{work.organization_name}</p>
                  </div>
                )}
                {(work.work_state || work.work_country) && (
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="text-slate-900">
                      {[work.work_state, work.work_country].filter(Boolean).join(", ")}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Professional Details */}
        {professional.rci_status && (
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-teal-700" />
                <h2 className="font-heading font-semibold text-lg text-slate-900">
                  Professional Details
                </h2>
              </div>
              <div className="space-y-3">
                {professional.years_of_experience && (
                  <div>
                    <p className="text-sm text-slate-500">Experience</p>
                    <p className="text-slate-900">{professional.years_of_experience} years</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">RCI Registration Status</p>
                  <p className="text-slate-900 capitalize">
                    {professional.rci_status?.replace('_', ' ')}
                  </p>
                </div>
                {professional.rci_number && (
                  <div>
                    <p className="text-sm text-slate-500">RCI Registration Number</p>
                    <p className="text-slate-900">{professional.rci_number}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Education */}
        {(academic.graduation_course || academic.pg_course || academic.phd_course) && (
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-5 h-5 text-teal-700" />
                <h2 className="font-heading font-semibold text-lg text-slate-900">
                  Education
                </h2>
              </div>
              <div className="space-y-4">
                {academic.phd_course && (
                  <div>
                    <p className="font-medium text-slate-900">{academic.phd_course}</p>
                    <p className="text-sm text-slate-600">{academic.phd_university}</p>
                    {academic.phd_year && (
                      <p className="text-sm text-slate-500">{academic.phd_year}</p>
                    )}
                  </div>
                )}
                {academic.pg_course && (
                  <>
                    {academic.phd_course && <Separator />}
                    <div>
                      <p className="font-medium text-slate-900">{academic.pg_course}</p>
                      <p className="text-sm text-slate-600">{academic.pg_university}</p>
                      {academic.pg_year && (
                        <p className="text-sm text-slate-500">{academic.pg_year}</p>
                      )}
                    </div>
                  </>
                )}
                {academic.graduation_course && (
                  <>
                    {(academic.phd_course || academic.pg_course) && <Separator />}
                    <div>
                      <p className="font-medium text-slate-900">{academic.graduation_course}</p>
                      <p className="text-sm text-slate-600">{academic.graduation_university}</p>
                      {academic.graduation_year && (
                        <p className="text-sm text-slate-500">{academic.graduation_year}</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Areas of Interest */}
        {member.areas_of_interest && (
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                Areas of Professional Interest
              </h2>
              <p className="text-slate-600 leading-relaxed">
                {member.areas_of_interest}
              </p>
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Documents */}
        {isAdminView && member.documents && member.documents.length > 0 && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-amber-600" />
                Uploaded Documents ({member.documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {member.documents.map((doc) => (
                  <div key={doc.file_id} className="border rounded-lg p-3 bg-slate-50">
                    <p className="font-medium text-sm capitalize">{doc.type?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
                    <div className="flex gap-2 mt-2">
                      <button 
                        onClick={() => handleViewDocument(doc)}
                        className="text-xs text-blue-700 hover:underline flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> View
                      </button>
                      <button 
                        onClick={() => handleDownloadDocument(doc)}
                        className="text-xs text-teal-700 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Application & Certificate Info */}
        {isAdminView && applicationInfo.application_id && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-indigo-600" />
                Application & Certificate History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Application ID</p>
                  <p className="font-mono text-xs">{applicationInfo.application_id}</p>
                </div>
                <div>
                  <p className="text-slate-500">Submitted</p>
                  <p>{applicationInfo.submission_date ? new Date(applicationInfo.submission_date).toLocaleDateString() : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Payment Status</p>
                  <Badge className={applicationInfo.payment_status === 'payment_received' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                    {applicationInfo.payment_status?.replace(/_/g, ' ') || 'N/A'}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500">Certificate Status</p>
                  <Badge className={applicationInfo.certificate_status === 'certificate_sent' ? 'bg-teal-100 text-teal-800' : 'bg-slate-100 text-slate-700'}>
                    {applicationInfo.certificate_status?.replace(/_/g, ' ') || 'N/A'}
                  </Badge>
                </div>
              </div>
              {member.certificate && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500 mb-2">Certificate File</p>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-teal-600" />
                    <span className="text-sm">{member.certificate.filename}</span>
                    <button 
                      onClick={() => handleViewDocument(member.certificate)}
                      className="text-xs text-blue-700 hover:underline"
                    >
                      View
                    </button>
                    <button 
                      onClick={() => handleDownloadDocument(member.certificate)}
                      className="text-xs text-teal-700 hover:underline"
                    >
                      Download
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Payment History */}
        {isAdminView && member.payment_history && member.payment_history.length > 0 && (
          <Card className="border border-slate-200 mb-6">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-green-600" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {member.payment_history.map((payment, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm">{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>₹{payment.amount}</TableCell>
                      <TableCell>
                        <Badge className={payment.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                          {payment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize text-sm">{payment.payment_type || payment.type || 'membership'}</TableCell>
                      <TableCell className="font-mono text-xs">{payment.razorpay_payment_id || payment.payment_id || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* ADMIN ONLY: Audit History */}
        {isAdminView && member.audit_history && member.audit_history.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="py-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5 text-slate-600" />
                Audit History (Last 20)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {member.audit_history.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm border-l-2 border-slate-200 pl-3 py-1">
                    <div className="flex-1">
                      <p className="font-medium capitalize">{entry.action?.replace(/_/g, ' ')}</p>
                      {entry.details && <p className="text-xs text-slate-500">{JSON.stringify(entry.details)}</p>}
                    </div>
                    <span className="text-xs text-slate-400">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MemberProfilePage;
